/*
 * Copyright (c) 2020 CalAmp Corp.  All Rights Reserved
 */
import {translate} from '../locale/locale';
import {GenericObject} from '../types/entities';

export enum EventCodeValueType {
  eventCode = 'eventCode',
  iOnEventName = 'iOnEventName',
  eventType = 'eventType'
};

/**
 * The string values represents event classifications and their translations.
 * The enum values are set to the translation keys which are typically used to
 * look up localized text strings.
 */
export enum EventClassification {
  Normal = 'Normal',
  Severe = 'Severe'
}
export const EventClassificationEntriesMap = {
  [EventClassification.Normal] : 'event-codes:eventClassification.normal',
  [EventClassification.Severe] : 'event-codes:eventClassification.severe'
};

export type EventCodeParams = {
  from: EventCodeValueType;
  val: string | number;
  source?: any;
  fallback?: string
};

export type EventCode = {
  eventCode: number,
  iOnEventName: string,
  eventType: string
  eventClassification: EventClassification
}

/**
 * Retrieves and returns the 'iOnEventName' for the given entry based on the provided
 * from argument (either EventCodeValueType.eventCode or EventCodeValueType.eventType).
 *
 * ```typescript
 *
 * import {getEventName, EventCodeValueType} from '@ion/core/src/services/EventCodes';
 *
 * getEventName({from: EventCodeValueType.eventCode, val: 137});
 * // 'Traffic Speed Violated'
 *
 * getEventName({from: EventCodeValueType.eventType, val: 'VIDEO_SPEED_VIOLATION'});
 * // 'Traffic Speed Violated'
 *
 * ```
 *
 */
export const getEventName = (args: EventCodeParams) =>
  getEntry(args, EventCodeValueType.iOnEventName);

/**
 * Retrieves and returns the 'eventCode' for the given entry based on the provided
 * from argument (either EventCodeValueType.iOnEventName or EventCodeValueType.eventType).
 *
 * ```typescript
 *
 * import {getEventCode, EventCodeValueType} from '@ion/core/src/services/EventCodes';
 *
 * getEventCode({from: EventCodeValueType.iOnEventName, val: 'Traffic Speed Violated'});
 * // 137
 *
 * getEventCode({from: EventCodeValueType.eventType, val: 'VIDEO_SPEED_VIOLATION'});
 * // 137
 *
 * ```
 *
 */
export const getEventCode = (args: EventCodeParams) =>
  getEntry(args, EventCodeValueType.eventCode);

/**
 * Retrieves and returns the 'eventType' for the given entry based on the provided
 * from argument (either EventCodeValueType.iOnEventName or EventCodeValueType.eventCode).
 *
 * ```typescript
 *
 * import {getEventType, EventCodeValueType} from '@ion/core/src/services/EventCodes';
 *
 * getEventType({from: EventCodeValueType.iOnEventName, val: 'Traffic Speed Violated'});
 * // 'VIDEO_SPEED_VIOLATION'
 *
 * getEventType({from: EventCodeValueType.eventCode, val: 137});
 * // 'VIDEO_SPEED_VIOLATION'
 *
 * ```
 *
 */
export const getEventType = (args: EventCodeParams) =>
  getEntry(args, EventCodeValueType.eventType);

/**
 * Retrieves and returns the event list.
 *
 * ```typescript
 *
 * import {getEventList} from '@ion/core/src/services/EventCodes';
 *
 * getEventList();
 * // [
 * //   {eventCode: 1, eventType: 'FOO', ionEventName: 'Event Foo'},
 * //   {eventCode: 2, eventType: 'BAR', ionEventName: 'Event Bar'},
 * //   {eventCode: 3, eventType: 'BAZ', ionEventName: 'Event Baz'},
 * //   ...
 * // ]
 *
 * ```
 *
 */
export const getEventList = () => {
  return flattenGroupedList(getGroupedEventList());
};

/**
 * Retrieves and returns the default event list.
 *
 * ```typescript
 *
 * import {getDefaultEventList} from '@ion/core/src/services/EventCodes';
 *
 * getDefaultEventList();
 * // [
 * //   {eventCode: 1, eventType: 'FOO', ionEventName: 'Event Foo'},
 * //   {eventCode: 3, eventType: 'BAZ', ionEventName: 'Event Baz'},
 * //   ...
 * // ]
 *
 * ```
 *
 */
export const getDefaultEventList = () => {
  return flattenGroupedListDefaults(getGroupedEventList(), getDefaultEventIds());
};

/**
 * Retrieves and returns the grouped event list.
 *
 * ```typescript
 *
 * import {getGroupedEventList} from '@ion/core/src/services/EventCodes';
 *
 * getGroupedEventList();
 * // [
 * //   {groupId: 'GROUP_1', groupLabel: 'Group 1', groupMembers: [
 * //     {eventCode: 1, eventType: 'FOO', ionEventName: 'Event Foo'},
 * //     {eventCode: 2, eventType: 'BAR', ionEventName: 'Event Bar'},
 * //     ...
 * //   ]},
 * //   {groupId: 'GROUP_2', groupLabel: 'Group 2', groupMembers: [
 * //     {eventCode: 3, eventType: 'BAZ', ionEventName: 'Event Baz'},
 * //     ...
 * //   ]},
 * //   ...
 * // ]
 *
 * ```
 *
 */
export const getGroupedEventList = () => {
  return translate('event-codes:list', {returnObjects: true});
};

export const getSCEventList = () => {
  return translate('shipment-event-codes:list', {returnObjects: true});
};
/**
 * @private
 */
const getDefaultEventIds = () => {
  return translate('event-codes:defaults', {returnObjects: true});
};

/**
 * @private
 */
const getEntry = ({from, val, source, fallback}: EventCodeParams, type: string) => {
  const list = source || getEventList();
  const entry = list?.find?.(entry => entry[from] === val);
  return entry?.[type] || (fallback || translate('common:notAvailable'));
};

/**
 * @private
 */
const flattenGroupedList = (groupedEventList: GenericObject[]) => {
  let list = [];
  groupedEventList?.forEach?.(entry => {
    if (entry?.groupMembers) {
      list = list.concat(flattenGroupedList(entry.groupMembers));
    } else {
      list.push(entry);
    }
  });
  return list;
};

/**
 * @private
 */
const flattenGroupedListDefaults = (
  groupedEventList: GenericObject[],
  defaultEventIds?: number[],
  defaultForceInclude: boolean = false
) => {
  let list = [];
  groupedEventList?.forEach?.(entry => {
    if (entry?.groupMembers) {
      list = list.concat(flattenGroupedListDefaults(
        entry.groupMembers,
        defaultEventIds,
        defaultForceInclude || defaultEventIds?.some?.(id => id === entry?.groupId)
      ));
    } else {
      if (defaultForceInclude || defaultEventIds?.some?.(id => id === entry?.eventCode)) {
        list.push(entry);
      }
    }
  });
  return list;
};

/**
 * The getEventCodesByClassification function returns an array of event codes that match
 * a specified event classification. The function can either take a specific list of events
 * as input or it will default to using a grouped event list.
 *
 * @param classification
 * @param eventList
 * @returns an array of event codes that match the specified classification.
 * If no events are found that match the classification, the function will return an empty array
 * ``` typescript
 * const eventList = [
 *  { eventCode: 1, eventClassification: "Normal" },
 *  { eventCode: 2, eventClassification: "Severe" },
 *  { eventCode: 3, eventClassification: "Normal" }
 * ];
 *
 * const normalEventCodes = getEventCodesByClassification("Normal", eventList);
 * // normalEventCodes will be [1, 3]
 *
 * const severeEventCodes = getEventCodesByClassification("Severe", eventList);
 * // severeEventCodes will be [2]
 *
 * ```
 */
export const getEventCodesByClassification = (
  classification = EventClassificationEntriesMap[EventClassification.Normal],
  eventList = getGroupedEventList(),
  field = 'eventCode'
): number[] | string [] => (
  eventList
    .filter(({eventClassification}) => eventClassification === classification)
    .map(event => event[field])
);
